// Server stats (Server settings → Stats; owners and admins only): what each server's room counts, and the shape of
// GET /api/servers/:id/stats. The room keeps small daily rollups (worker/src/stats.ts), updated after messages and
// reactions are stored, never on the way to the ack; reads add those rows up when the page is opened.
import { CUSTOM_EMOJI_TOKEN, parseEmojiToken } from "./emoji.ts";

export type StatsRange = "7d" | "30d" | "all";
export const STATS_RANGES: StatsRange[] = ["7d", "30d", "all"];

/** A rollup day: whole days since 1970-01-01, UTC. */
export const statsDay = (ts: number) => Math.floor(ts / 86_400_000);

/**
 * An emoji's key in the stats: the Unicode emoji itself, "c:<id>" for a server emoji (so a rename doesn't split its
 * counts), and "s:<id>" for a sticker.
 */
export const customKey = (id: string) => `c:${id}`;
export const stickerKey = (id: string) => `s:${id}`;

/** The key for a reaction's emoji (a Unicode emoji, or a server emoji's "<:name:id>" token). */
export function reactionKey(emoji: string): string {
  const custom = parseEmojiToken(emoji);
  return custom ? customKey(custom.id) : emoji;
}

// One emoji as people see it: a flag (two regional indicators), a keycap, or a pictograph shown as emoji (by default,
// or with the emoji variation selector), with an optional skin tone and joined (ZWJ) parts. Text-style symbols such as
// ™ or © on their own don't count.
const UNICODE_EMOJI =
  /\p{Regional_Indicator}{2}|[#*0-9]️?⃣|(?:\p{Emoji_Presentation}|\p{Extended_Pictographic}️)\p{Emoji_Modifier}?(?:‍\p{Extended_Pictographic}️?\p{Emoji_Modifier}?)*/gu;
// Code (```blocks``` and `spans`) isn't emoji use; tokens other than custom emoji (<@id>, <#id>) hold none anyway.
const CODE = /(`{3,})[\s\S]*?\1|`[^`\n]*`/g;

/**
 * The distinct emoji a stored message body uses, as stats keys. A message with 🎉🎉🎉 counts 🎉 once: popularity is
 * "how many messages used it", which one enthusiastic message can't swamp.
 */
export function emojiInBody(body: string): string[] {
  if (!body) return [];
  const text = body.includes("`") ? body.replace(CODE, " ") : body;
  const keys = new Set<string>();
  if (text.includes("<")) for (const m of text.matchAll(CUSTOM_EMOJI_TOKEN)) keys.add(customKey(m[3]!));
  // Custom tokens hold only ASCII, so scanning the whole text for Unicode emoji is safe.
  for (const m of text.matchAll(UNICODE_EMOJI)) keys.add(m[0]);
  return [...keys];
}

/** How an emoji in the stats is drawn: Unicode as text, server emoji and stickers by id (null name: since deleted). */
export interface StatsItem {
  key: string;
  kind: "unicode" | "custom" | "sticker";
  /** Server emoji and stickers: their id (pictures at emojiUrl / stickerUrl). */
  id?: string;
  /** Server emoji and stickers: the current name; null once deleted. */
  name?: string | null;
  animated?: boolean;
}

export interface StatsCount {
  key: string;
  n: number;
}

/**
 * GET /api/servers/:id/stats?range=7d|30d|all (owners and admins). People are ids (names come from the member list);
 * channels are text channels only, with their threads counted in. DMs count toward a person's totals and the
 * server's, and are never listed or named.
 */
export interface ServerStats {
  range: StatsRange;
  /** First day counted (ms, UTC midnight); null for all time. */
  since: number | null;
  /** True while the room is still counting messages from before stats existed (the numbers are still rising). */
  backfilling: boolean;
  totals: { messages: number; people: number; emoji: number; reactions: number; stickers: number; bloomAsks: number; bloomBuilds: number };
  /** Messages per day (ms of each UTC day), oldest first; days with none are left out. */
  daily: { day: number; messages: number }[];
  people: { userId: string; messages: number; activeDays: number; lastActive: number; emoji: number; reactions: number; stickers: number; bloomAsks: number; bloomBuilds: number }[];
  /** Text channels (threads included). channelId null: channels deleted since, added up. */
  channels: { channelId: string | null; messages: number; emoji: number; reactions: number }[];
  /** Most used emoji, in messages and as reactions. */
  emoji: { key: string; messages: number; reactions: number }[];
  emojiByChannel: { channelId: string | null; top: StatsCount[] }[];
  emojiByPerson: { userId: string; top: StatsCount[] }[];
  stickers: StatsCount[];
  stickersByPerson: { userId: string; top: StatsCount[] }[];
  /** How to draw every key above. */
  items: Record<string, StatsItem>;
  /** Apps bloomed in this server, all time (the range doesn't apply): blooming threads started and apps published. */
  apps: { started: number; published: number; byPerson: { userId: string; started: number; published: number }[] };
}

/**
 * GET /api/admin/metrics (header x-admin-token): Bloom-wide numbers for the SOMA Labs admin console. Never message
 * content, names or emails: servers are ids, people are only counted.
 */
export interface AdminMetrics {
  product: "bloom";
  generatedAt: number;
  build: string | null;
  health: { ok: boolean; d1Ms: number; rooms: { asked: number; answered: number; failed: number }; lastBackup: { at: string | null; ageHours: number | null }; latencyTargetsMet: boolean | null };
  rooms: { total: number; active24h: number; active7d: number; active30d: number };
  latency: { overall: AdminLatencyWindows; servers: { serverId: string; windows: AdminLatencyWindows }[] };
  usage: { days: { day: string; messages: number; activeMembers: number; servers: number }[]; members: number };
}

export type AdminLatencyWindow = "24h" | "7d" | "30d";
export type AdminLatencyWindows = Record<AdminLatencyWindow, Record<string, { count: number; p50: number; p95: number; p99: number; target: number | null }>>;

/** The chat-feel targets (PRD; 95th percentile), by latency sample kind. */
export const LATENCY_TARGETS: Partial<Record<string, number>> = { echo: 150, typing: 100, channel_switch: 100, cold_start: 1500, reconnect: 1000 };

// ---- Channel stats and My stats (#306): the past week, from the same rollups ----

/** Days in the week: 7 complete UTC days. The rollups are UTC days, so a person's own midnight is not used. */
export const WEEK_DAYS = 7;
/** Rows in each "top" list on the channel and personal pages. */
export const WEEK_TOP = 5;

/**
 * The week for `now`, in UTC days. "This week" is the 7 complete days before today (`since` .. `today - 1`), and "the
 * week before" the 7 complete days under that (`prevSince` .. `since - 1`): the two are the same length, so the change
 * between them is fair at any hour. Today is kept apart as "today so far". The top lists cover `since` .. `today`.
 */
export function statsWeek(now: number): { today: number; since: number; prevSince: number } {
  const today = statsDay(now);
  return { today, since: today - WEEK_DAYS, prevSince: today - 2 * WEEK_DAYS };
}

/** One UTC day of a week chart (ms of its UTC midnight). */
export interface StatsDay {
  day: number;
  messages: number;
}

/** The busiest day of a week: the one with most messages, the earliest on a tie; null when the week is silent. */
export function busiestDay(days: StatsDay[]): StatsDay | null {
  let best: StatsDay | null = null;
  for (const d of days) if (d.messages > (best?.messages ?? 0)) best = d;
  return best;
}

/**
 * GET /api/servers/:id/channels/:channelId/stats (any member of the server; a text channel only). The week is
 * `statsWeek`: the headline count and its change use the 7 complete UTC days; the top lists add today so far. Messages in the channel's threads count under the channel. No DM is ever a channel here:
 * asking for a DM's id is "not found", as if it did not exist. People are ids (names come from the member list).
 */
export interface ChannelStats {
  /** First day of the week (ms, UTC midnight). */
  since: number;
  backfilling: boolean;
  /** The 7 complete days, oldest first, quiet days included (always WEEK_DAYS entries). */
  days: StatsDay[];
  /** Today so far (a partial day, so it is not in `days` or `messages`). */
  today: StatsDay;
  /** The 7 complete days added up. */
  messages: number;
  /** The 7 complete days before those. */
  previousMessages: number;
  /** Top WEEK_TOP by messages over the 7 complete days and today so far; equal counts go by id, so the order never changes between reads. */
  chattiest: { userId: string; messages: number }[];
  /** Top WEEK_TOP emoji, in messages and as reactions, most used first. */
  emoji: { key: string; messages: number; reactions: number }[];
  stickers: StatsCount[];
  bloomAsks: number;
  /** How to draw every key above. */
  items: Record<string, StatsItem>;
}

/** Where a person's messages were posted, as My stats shows it. A DM is only ever "dm": no id, no other person. */
export interface MyChannelCount {
  kind: "channel" | "dm" | "deleted";
  /** Set for kind "channel" only. */
  channelId?: string;
  messages: number;
}

/**
 * GET /api/servers/:id/stats/me (the signed-in member; never another person's). The past week. DMs count in the
 * totals and show as one "Direct messages" row.
 */
export interface MyStats {
  since: number;
  backfilling: boolean;
  /** The 7 complete days, oldest first. */
  days: StatsDay[];
  /** Today so far (not in `days` or `messages`). */
  today: StatsDay;
  messages: number;
  previousMessages: number;
  /** Top WEEK_TOP places by messages over the 7 complete days and today so far (a text channel, "Direct messages", or "Deleted channels"). */
  channels: MyChannelCount[];
  emoji: { key: string; messages: number; reactions: number }[];
  stickers: StatsCount[];
  /** Reactions you added over the 7 complete days and today so far. */
  reactionsGiven: number;
  /** Reactions others added in that time to your messages (not your own, not a bot's, not on app cards or system lines). */
  reactionsGot: number;
  items: Record<string, StatsItem>;
}

/**
 * How this week compares with the week before, in words, with both raw numbers (never a ratio alone). Every client
 * shows this text; "" when both weeks are silent.
 */
export function weekChangeText(now: number, before: number): string {
  if (!before) return now ? "No messages the week before" : "";
  if (now === before) return `Same as the week before (${before})`;
  return `${now > before ? "Up" : "Down"} ${Math.abs(now - before)} from ${before} the week before`;
}
